import React, { useMemo, useState, useCallback } from 'react';
import {
  Flame, AlertTriangle, Phone, MessageSquare, Mail, Calendar, ChevronRight, RefreshCw,
} from 'lucide-react';
import { safeAsync, Badge, emptyMsg, cardWrap, neuShadow, btnSm, CONTACT_TYPES } from './crmShared';
import CrmFilterBar from '../../components/CrmFilterBar/CrmFilterBar';
import FollowUpBadge from '../../components/FollowUpBadge';
import { followUpState, compareFollowUp } from '../../lib/followUpStatus';
import { matchesNameQuery } from '../../lib/nameSearch';

/* ═══════════════════════════════════════════════════════════════════════════
   DailyHitListTab — daily action queue surfacing two ranked lists:
     1. Top 10 HOT leads (highest Lead Health Score, ready to close)
     2. Top 10 AT-RISK leads (any AT_RISK flag triggered, needs rescue)

   Uses the _leadScore / _leadBand / _atRiskFlags / _scoreCategories fields
   added to crm:contacts:list response by the lead-health-score PR.

   Quick actions per row: call / SMS / email / open detail. Each action
   stops the row's onClick so they don't dual-fire.
   ═══════════════════════════════════════════════════════════════════════════ */

const HIT_LIST_SIZE = 10;

const AT_RISK_LABELS = {
  no_contact_72h: 'No contact 72h+',
  stuck_in_stage_5d: 'Stuck in stage 5d+',
  high_value_no_response: 'High value, no response',
  missed_consult: 'Missed consult',
};

/*
 * ⛔⛔ WHO MAY LOOK ACROSS REPS — Card P114 (D4).
 *
 * Ryland, 23 Oct: the Daily Hit List "shows every rep's leads". A daily
 * action queue that opens on the whole company is a queue nobody can work,
 * so it now opens on the signed-in user's own leads.
 *
 * ⚠ WHY A ROLE LIST AND NOT A PERMISSION. There is no permission that means
 * "oversees other reps" — `crm:read` is held by every CRM role including
 * `sales` and `concierge` (database/rbac.js), so gating on it would be
 * gating on nothing. These four are the roles with company-wide commercial
 * oversight in ROLE_PERMISSIONS; `sales`, `concierge` and `patient_experience`
 * work their own queues and get the self-scoped bar.
 *
 * ⚠⚠ AND IT IS PRESENTATIONAL. `crm:contacts:list` (routes/crm.js:1069) is
 * gated on `crm:read` with NO owner scoping, so every contact is already in
 * the browser for anyone who can open this tab. This decides what the bar
 * OFFERS and where it STARTS — it is not an access control and must never be
 * cited as one. Server-side scoping is proposed in the PR body.
 */
const CROSS_REP_ROLES = new Set(['admin', 'sysadmin', 'executive', 'business']);

export default function DailyHitListTab({ contacts, allUsers, currentUser, onContactClick, loadData }) {
  const canSeeAllReps = CROSS_REP_ROLES.has(currentUser?.role);

  /*
   * ⚠ THE INITIAL VALUE MATCHES WHAT THE BAR WILL EMIT ON ITS FIRST RENDER.
   * CrmFilterBar seeds its own select from `defaultOwner` and emits in an
   * effect; starting this at `{owner: null}` would render one frame of every
   * rep's leads before the effect lands. Harmless-looking, and exactly the
   * flash of another rep's queue this card exists to remove.
   */
  const [barFilter, setBarFilter] = useState({
    owner: currentUser?.id != null ? Number(currentUser.id) : null,
    search: '',
  });

  const filteredContacts = useMemo(() => {
    let list = contacts || [];
    if (barFilter.owner) list = list.filter(c => c.assigned_to === barFilter.owner);
    if (barFilter.search) {
      // Full-name search: every token must appear, any order (card
      // CRM-FULLNAME-SEARCH). `${first} ${last}`.includes() found
      // "Joe Bubba" but never "Bubba Joe".
      list = list.filter(c =>
        matchesNameQuery([c.first_name, c.last_name, c.organization], barFilter.search));
    }
    return list;
  }, [contacts, barFilter]);

  // Filter to scored contacts only (skipScore=true callers won't have _leadScore)
  const scored = useMemo(
    () => filteredContacts.filter((c) => typeof c._leadScore === 'number'),
    [filteredContacts]
  );

  // Detect the silent "server tried to score but failed" mode. The
  // server-side catch in routes/crm.js tags every row with _scoringFailed
  // when the lead-score Promise.all bails (RDS cold-start, Bedrock
  // throttle, transient query timeout). We need to render a real banner
  // for this case instead of looking indistinguishable from "user has 0
  // contacts."
  const scoringFailed = useMemo(
    () => filteredContacts.some((c) => c._scoringFailed === true),
    [filteredContacts]
  );

  /*
   * ⛔⛔ OVERDUE FIRST — Card P117 PR-A (Maria, 25 Oct).
   *
   * Both lists were ranked by lead score alone. A score says how PROMISING a
   * lead is; it says nothing about whether the rep has already missed the
   * commitment they made to call it. Maria works this screen to decide what
   * to do next, so the thing she has broken a promise about has to come first.
   *
   * ⚠ SCORE IS THE TIE-BREAK, NOT THE SORT. Within a follow-up state the
   * original ranking is preserved exactly, so this reorders the list without
   * discarding the signal it already had. `compareFollowUp` additionally puts
   * the LONGEST-overdue above the barely-overdue — "overdue" alone is not a
   * priority once a dozen rows carry it.
   *
   * ⚠ The classification is computed ONCE per contact and threaded through,
   * rather than recomputed inside the comparator. A comparator that builds a
   * Date per comparison is O(n log n) clock reads, and — worse — can observe
   * midnight passing mid-sort and produce an inconsistent ordering.
   */
  const rank = useCallback((list) => list
    .map((c) => ({ c, fu: followUpState(c.next_follow_up_date) }))
    .sort((x, y) => compareFollowUp(x.fu, y.fu) || (y.c._leadScore - x.c._leadScore))
    .map((x) => x.c), []);

  const hotLeads = useMemo(
    () => rank(scored.filter((c) => c._leadBand === 'hot' && !c._isAtRisk)).slice(0, HIT_LIST_SIZE),
    [scored, rank]
  );

  const atRiskLeads = useMemo(
    () => rank(scored.filter((c) => c._isAtRisk)).slice(0, HIT_LIST_SIZE),
    [scored, rank]
  );

  /* Counts for the header — the number Maria actually triages against. */
  const overdueCount = useMemo(
    () => scored.filter((c) => followUpState(c.next_follow_up_date).state === 'overdue').length,
    [scored]
  );

  const totalHot = scored.filter((c) => c._leadBand === 'hot').length;
  const totalAtRisk = scored.filter((c) => c._isAtRisk).length;

  return (
    <div>
      {/* ═══ Unified Filter Bar (Task #230) ═══ */}
      <CrmFilterBar
        resource="contacts"
        allUsers={allUsers}
        currentUser={currentUser}
        /* Card P114 (D4) — open on MY leads; managers may widen it. */
        defaultOwner={currentUser?.id ?? null}
        ownerScope={canSeeAllReps ? 'all' : 'self'}
        onFilterChange={(f) => setBarFilter({ owner: f.owner || null, search: f.search || '' })}
      />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Flame size={18} color="#DC2626" /> Daily Hit List
        </h3>
        <button onClick={loadData} style={btnSm}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Scoring-failed banner — visible at the top of the tab when the
          server-side lead-score computation returned with _scoringFailed
          markers. Common cause: RDS cold-start after hibernation. The fix
          from the user's side is a Refresh after the DB warms up. */}
      {scoringFailed && (
        <div style={{
          background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
          padding: '12px 16px', marginBottom: 16, color: '#78350f', fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <AlertTriangle size={18} color="#d97706" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              Lead scoring temporarily unavailable
            </div>
            <div style={{ fontSize: 12, color: '#92400e' }}>
              Your contacts loaded ({(contacts || []).length} total), but the
              scoring service didn't respond in time — usually a database
              warming up after a quiet period. Try Refresh in a moment.
            </div>
          </div>
          <button onClick={loadData} style={{
            background: '#d97706', color: '#fff', border: 'none', borderRadius: 6,
            padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
          }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      )}

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <SummaryCard
          label="Hot leads"
          value={totalHot}
          color="#15803D"
          bg="#DCFCE7"
          icon={<Flame size={16} color="#15803D" />}
        />
        <SummaryCard
          label="At-risk"
          value={totalAtRisk}
          color="#B45309"
          bg="#FEF3C7"
          icon={<AlertTriangle size={16} color="#B45309" />}
        />
        {/* Card P117 PR-A — the number Maria triages against, given the
            same weight as Hot and At-risk rather than buried in the rows. */}
        <SummaryCard
          label="Overdue follow-ups"
          value={overdueCount}
          color="#991B1B"
          bg="#FEE2E2"
          icon={<AlertTriangle size={16} color="#991B1B" />}
        />
        <SummaryCard
          label="Scored contacts"
          value={scored.length}
          color="#0369A1"
          bg="#E0F2FE"
        />
      </div>

      {/* Hot leads */}
      <Section
        title="🟢 Top 10 Hot Leads"
        subtitle="Ready to close — prioritize first"
        emptyText="No hot leads right now. Keep working the active list."
        items={hotLeads}
        onContactClick={onContactClick}
        kind="hot"
      />

      {/* At-risk leads */}
      <div style={{ marginTop: 24 }}>
        <Section
          title="⚠️ Top 10 At-Risk Leads"
          subtitle="Slipping or stuck — rescue these before they go cold"
          emptyText="No at-risk leads. Nice work staying on top of follow-ups."
          items={atRiskLeads}
          onContactClick={onContactClick}
          kind="at-risk"
        />
      </div>

      {/* Empty-state — only fires when scoring DIDN'T fail. The
          scoringFailed banner above handles that case with its own copy
          and Refresh button. This block remains for the legitimate
          "user has 0 contacts assigned" or "scores genuinely all zero"
          case where we want a softer informational message. */}
      {scored.length === 0 && !scoringFailed && (
        <div style={{ ...cardWrap, padding: 24, textAlign: 'center', marginTop: 24 }}>
          <p style={{ color: '#6C757D', fontSize: 13, marginBottom: 8 }}>
            <strong>No scored leads in the daily hit list yet.</strong>
          </p>
          <p style={{ color: '#6C757D', fontSize: 12 }}>
            {(!contacts || contacts.length === 0)
              ? 'Your contacts list is empty — add some leads on the Contacts tab and they\'ll appear here once scored.'
              : `${filteredContacts.length} contacts match your filters, but none currently meet the hot or at-risk thresholds. Keep working the list — interactions and pipeline progress feed the scoring.`}
          </p>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, bg, icon }) {
  return (
    <div style={{
      background: bg,
      borderRadius: 10,
      padding: '10px 16px',
      minWidth: 130,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      {icon}
      <div>
        <div style={{ fontSize: 10, color: '#6C757D', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, items, onContactClick, kind, emptyText }) {
  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#212529' }}>{title}</h4>
        <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>{subtitle}</div>
      </div>

      {items.length === 0 ? (
        <div style={{ ...cardWrap, padding: 16, textAlign: 'center' }}>
          <span style={{ color: '#6C757D', fontSize: 12 }}>{emptyText}</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((c, i) => (
            <HitListRow key={c.id} contact={c} rank={i + 1} kind={kind} onContactClick={onContactClick} />
          ))}
        </div>
      )}
    </div>
  );
}

function HitListRow({ contact: c, rank, kind, onContactClick }) {
  const ct = CONTACT_TYPES[c.contact_type] || { label: c.contact_type || '?', color: '#6C757D' };

  const bandColor = c._leadBand === 'hot' ? '#15803D' : c._leadBand === 'active' ? '#B45309' : '#DC2626';
  const bandLabel = c._leadBand === 'hot' ? 'Hot' : c._leadBand === 'active' ? 'Active' : 'At Risk';

  const handleRowClick = () => {
    if (onContactClick) onContactClick(c.id);
  };

  return (
    <div
      onClick={handleRowClick}
      style={{
        ...cardWrap,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        outline: kind === 'at-risk' ? '2px solid #FBBF24' : 'none',
        outlineOffset: -2,
      }}
    >
      {/* Rank */}
      <div style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: kind === 'hot' ? '#DCFCE7' : '#FEF3C7',
        color: kind === 'hot' ? '#15803D' : '#B45309',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}>
        {rank}
      </div>

      {/* Score chip */}
      <div style={{
        background: bandColor,
        color: 'white',
        fontSize: 12,
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: 999,
        minWidth: 38,
        textAlign: 'center',
        flexShrink: 0,
      }}>
        {c._leadScore}
      </div>

      {/* Name + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#212529' }}>
          {c.last_name || ''}{c.last_name && c.first_name ? ', ' : ''}{c.first_name || ''}
        </div>
        <div style={{ fontSize: 11, color: '#6C757D', display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2, alignItems: 'center' }}>
          {/* Card P117 PR-A — FIRST in the meta line: it is the field Maria
              triages on, and the row is scanned left to right. */}
          <FollowUpBadge value={c.next_follow_up_date} compact />
          <Badge text={ct.label} color={ct.color} />
          <span>{bandLabel}</span>
          {c.pipeline_stage && <span>· {c.pipeline_stage}</span>}
          {c.organization && <span>· {c.organization}</span>}
          {/* AT-RISK flag chips */}
          {c._isAtRisk && (c._atRiskFlags || []).slice(0, 2).map((flag) => (
            <span key={flag} style={{
              background: '#FBBF24',
              color: '#78350F',
              padding: '1px 7px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 600,
            }}>
              {AT_RISK_LABELS[flag] || flag}
            </span>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {c.phone && (
          <button
            onClick={(e) => { e.stopPropagation(); window.open(`tel:${c.phone}`); }}
            style={{ ...btnSm, padding: '4px 8px' }}
            title={`Call ${c.phone}`}
          >
            <Phone size={12} color="#62BB46" />
          </button>
        )}
        {c.phone && (
          <button
            onClick={(e) => { e.stopPropagation(); window.open(`sms:${c.phone}`); }}
            style={{ ...btnSm, padding: '4px 8px' }}
            title={`SMS ${c.phone}`}
          >
            <MessageSquare size={12} color="#2E8B98" />
          </button>
        )}
        {c.email && (
          <button
            onClick={(e) => { e.stopPropagation(); window.open(`mailto:${c.email}`); }}
            style={{ ...btnSm, padding: '4px 8px' }}
            title={`Email ${c.email}`}
          >
            <Mail size={12} color="#F26522" />
          </button>
        )}
      </div>

      <ChevronRight size={14} color="#94A3B8" />
    </div>
  );
}
